import { useEffect, useMemo, useState } from "react";
import {
  DASH_ML,
  DROP_ML,
  flavourColour,
  fromScratchLines,
  hasTalk,
  lineText,
  recipeCard,
  grossProfit,
  isHouseMade,
  NEUTRAL_COLOUR,
  normaliseTags,
  publicRecipe,
  publicRecipeText,
  SPEC_STATUSES,
  specTotals,
  toFlavourFamily,
  standardDrinks,
  type Ingredient,
  type LineUnit,
  type Spec,
  type SpecLine,
  type SpecOverride,
  type SpecStatus,
  variantsOf,
} from "@baros/core";
import { api, ApiError, type Library } from "./api.js";
import type { ProposedBuild } from "./Chat.js";
import { money, pct, shortDate, STATUS_LABEL, statusColour } from "./format.js";
import { BatchTool } from "./BatchTool.js";
import { recipeDiff } from "@baros/core";
import { RecipeCard } from "./RecipeCard.js";
import { Scaled } from "./Scaled.js";
import { SpecAroma } from "./SpecAroma.js";

/** What the editor holds; the item page turns it into a recipe save (PUT /v1/recipes/:id). */
export type SpecDraft = Pick<
  Spec,
  | "name" | "status" | "style" | "tags" | "method" | "glass" | "ice" | "garnish" | "batch"
  | "creator" | "added" | "history" | "notes" | "sundries" | "sheetGp" | "lines" | "version" | "talk"
> & { readonly variantOf: string | null };

interface Props {
  readonly spec: Spec;
  readonly seed?: ProposedBuild | undefined;
  readonly startEditing: boolean;
  readonly library: Library;
  readonly override: SpecOverride | undefined;
  readonly canEdit: boolean;
  readonly canPrice: boolean;
  readonly token: string;
  readonly onBack: () => void;
  readonly onPrev: (() => void) | undefined;
  readonly onNext: (() => void) | undefined;
  readonly onSave: (draft: SpecDraft) => Promise<Spec>;
  readonly onSavePrice: (price: number | null, available: boolean) => Promise<void>;
  /** Absent when the organisation does not have the development chat (D14). */
  readonly onRevise?: (() => void) | undefined;
  /** The batch calculator, when the organisation has it (D14). */
  readonly canBatch?: boolean;
  /** Open another spec: a variant, or the primary. */
  readonly onOpen: (specId: string) => void;
  /** Create a variant of this spec, copied from it, and open it for editing. */
  readonly onNewVariant: (name: string) => Promise<void>;
  /** Open a house-made ingredient the recipe pours on Prep › Batches. */
  readonly onOpenBatch?: ((ingredientId: string) => void) | undefined;
  /** May copy the drink as a public caption (`spec:share`). */
  readonly canShare: boolean;
  /** The venue, to sign a shared caption with. */
  readonly placeName?: string | undefined;
  /**
   * Rendered as one recipe of an item (#180): the item's own fields — name,
   * story, style, tags — and the variant family are the item page's, so
   * the editor and the aside leave them out. `house` says whether this is
   * the item's House recipe; `houseLines` is the House build a variant is
   * shown against.
   */
  /**
   * Set when the editor is a tab on an item's page. `ownTags` are this
   * recipe's own, which the editor edits — the item's are edited on the
   * page above, and `spec.tags` is the two of them merged for reading.
   */
  readonly withinItem?:
    | {
        readonly house: boolean;
        readonly houseLines: readonly SpecLine[] | null;
        readonly ownTags: readonly string[];
      }
    | undefined;
}

/** Put text on the clipboard; false when the browser will not. */
async function copy(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

function draftOf(spec: Spec, seed?: ProposedBuild, ownTags?: readonly string[]): SpecDraft {
  const base: SpecDraft = {
    name: spec.name,
    status: spec.status,
    style: spec.style,
    // On an item's page the editor owns the recipe's tags alone; the
    // item's are edited above it, and `spec.tags` is both merged (#188).
    tags: ownTags ?? spec.tags ?? [],
    method: spec.method,
    glass: spec.glass,
    ice: spec.ice,
    garnish: spec.garnish,
    batch: spec.batch,
    creator: spec.creator,
    added: spec.added,
    history: spec.history,
    notes: spec.notes,
    sundries: spec.sundries,
    sheetGp: spec.sheetGp,
    talk: spec.talk ?? null,
    lines: spec.lines,
    version: spec.version,
    variantOf: spec.variantOf ?? null,
  };
  if (!seed) return base;
  return {
    ...base,
    method: seed.method || base.method,
    glass: seed.glass || base.glass,
    ice: seed.ice || base.ice,
    garnish: seed.garnish || base.garnish,
    notes: [base.notes, seed.note ? `Development chat: ${seed.note}` : ""].filter(Boolean).join("\n"),
    // The batch the chat planned, unless the spec already names one.
    batch: base.batch || seed.batchName || "",
    // In the units the chat wrote them in, so a dash arrives as a dash. The
    // batch, if any, was folded into a house-made ingredient before this.
    lines: seed.lines.map((l) =>
      l.unit && l.unit !== "ml"
        ? { ingredientId: l.ingredientId, ml: l.ml, unit: l.unit, ...(l.qty !== undefined ? { qty: l.qty } : {}) }
        : { ingredientId: l.ingredientId, ml: l.ml, unit: "ml" as const },
    ),
  };
}

/** Where this browser keeps the reader's choice of card or exploded build. */
const VIEW_KEY = "baros.recipeView";

function colourOf(ing: Ingredient | undefined): string {
  if (!ing || ing.kind === "dilution") return NEUTRAL_COLOUR;
  return toFlavourFamily(ing.aromaFamily) ? flavourColour(ing.aromaFamily) : NEUTRAL_COLOUR;
}

function isTallGlass(glass: string): boolean {
  return /highball|collins|mug|ceramic|rocks/i.test(glass);
}

export function SpecView(p: Props) {
  const [editing, setEditing] = useState(p.startEditing);
  const [draft, setDraft] = useState<SpecDraft>(() => draftOf(p.spec, p.seed, p.withinItem?.ownTags));
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(null);
  const [versions, setVersions] = useState<Spec[] | null>(null);
  const [price, setPrice] = useState<string>(p.override?.price?.toString() ?? "");
  const [available, setAvailable] = useState<boolean>(p.override?.available ?? p.spec.status === "available");
  const [variantName, setVariantName] = useState<string | null>(null);
  const [variantErr, setVariantErr] = useState<string | null>(null);
  /** What the left pane shows when not editing: the exploded build or the calculators. */
  const [tool, setTool] = useState<"build" | "batch">("build");
  // The card is the recipe as it is made; the exploded glass is the drawing
  // from the prototype. The choice is the reader's and this browser keeps it.
  const [view, setView] = useState<"card" | "exploded">(() => {
    try {
      return window.localStorage.getItem(VIEW_KEY) === "exploded" ? "exploded" : "card";
    } catch {
      return "card";
    }
  });
  const chooseView = (next: "card" | "exploded") => {
    setView(next);
    try {
      window.localStorage.setItem(VIEW_KEY, next);
    } catch {
      // Storage blocked: the choice lasts until the page is reloaded.
    }
  };
  const [sharing, setSharing] = useState(false);
  // The lines are what is served (#180). A drink that pours a batch can be
  // read from scratch, with the batch opened into what it is made of.
  const [fromScratch, setFromScratch] = useState(false);
  /** Text the browser would not copy, shown for the person to copy by hand. */
  const [manualCopy, setManualCopy] = useState<string | null>(null);

  // The family this spec belongs to: its primary, and the primary's variants.
  const specsById = useMemo(
    () => new Map(p.library.specs.map((s) => [s.specId, s])),
    [p.library.specs],
  );
  const primary = p.spec.variantOf ? specsById.get(p.spec.variantOf) : undefined;
  const family = useMemo(
    () => variantsOf(p.library.specs, p.spec.variantOf || p.spec.specId),
    [p.library.specs, p.spec.variantOf, p.spec.specId],
  );
  const ownVariants = p.spec.variantOf ? [] : family;
  // What this spec could be filed under: any other primary. None at all when
  // it has variants of its own; one level, see `variantProblem`.
  const primaries = useMemo(
    () =>
      p.library.specs
        .filter((s) => !s.variantOf && s.specId !== p.spec.specId)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [p.library.specs, p.spec.specId],
  );
  const showFamily = !p.withinItem && (Boolean(primary) || ownVariants.length > 0 || (p.canEdit && !p.spec.variantOf));

  const ingredients = useMemo(
    () => new Map(p.library.ingredients.map((i) => [i.ingredientId, i])),
    [p.library.ingredients],
  );
  const sortedIngredients = useMemo(
    () => [...p.library.ingredients].sort((a, b) => a.name.localeCompare(b.name)),
    [p.library.ingredients],
  );
  // What a variant does differently from House (#180).
  const vsHouse = p.withinItem && !p.withinItem.house && p.withinItem.houseLines ? recipeDiff(p.withinItem.houseLines, p.spec.lines, ingredients) : null;

  useEffect(() => {
    api.versions(p.token, p.spec.specId).then((v) => setVersions(v.versions)).catch(() => setVersions([]));
  }, [p.token, p.spec.specId, p.spec.version]);

  const shown = editing ? draft : p.spec;

  /**
   * The chips above the name are a read-out of everything the recipe is
   * filed under, so they stay the item's tags plus the recipe's even while
   * the editor holds only the recipe's. Taking the recipe's own out of the
   * spec view leaves the item's, which the page above edits.
   */
  const ownTags = p.withinItem?.ownTags;
  const headTags = !editing || !ownTags
    ? (shown.tags ?? [])
    : normaliseTags([...(p.spec.tags ?? []).filter((t) => !ownTags.includes(t)), ...draft.tags]);
  const totals = specTotals(shown, ingredients);
  const priceNum = p.override?.price ?? null;
  const gp = priceNum ? grossProfit(priceNum, totals.cost) : null;
  const pourCost = priceNum ? (totals.cost / (priceNum / 1.1)) * 100 : null;
  // The house-made ingredients the build pours, and the build with them opened up.
  const housemade = [...new Set(shown.lines.map((l) => l.ingredientId))]
    .map((id) => ingredients.get(id))
    .filter((ing): ing is Ingredient => ing !== undefined && isHouseMade(ing));
  const scratch = housemade.length > 0 ? fromScratchLines(shown.lines, ingredients) : null;
  const viewLines = !editing && fromScratch && scratch ? scratch : shown.lines;
  const viewTotals = viewLines === shown.lines ? totals : specTotals({ lines: viewLines, sundries: shown.sundries }, ingredients);

  // ---- the exploded glass, as in the prototype
  const tall = isTallGlass(shown.glass);
  const bottom = 222;
  const maxFill = tall ? 180 : 168;
  const refVol = tall ? 240 : 130;
  const fillPx = maxFill * Math.min(1, viewTotals.volumeMl / Math.max(refVol, 1));
  const rx = tall ? 118 : 96;
  const rw = tall ? 156 : 200;
  const edge = tall ? 274 : 296;
  const lines = viewLines;
  const pitch = lines.length > 7 ? 42 : lines.length > 5 ? 52 : 64;
  const top0 = lines.length > 7 ? 46 : 58;
  let cy = bottom;
  const strata = lines.map((l, i) => {
    const h = viewTotals.volumeMl > 0 ? Math.max(2.5, (l.ml / viewTotals.volumeMl) * fillPx) : 0;
    const y = cy - h;
    cy = y;
    const mid = y + h / 2;
    const ty = top0 + 2 + (lines.length - 1 - i) * pitch + 15;
    const ing = ingredients.get(l.ingredientId);
    const lineCost = l.ml * (ing?.costPerMl ?? 0);
    return {
      key: `${l.ingredientId}-${i}`,
      y, h, mid, ty,
      fill: colourOf(ing),
      tag: "ABCDEFGHIJKLMNOP"[i] ?? "",
      name: (ing?.name ?? l.ingredientId).toUpperCase(),
      detail: `${l.unit === "dash" || l.unit === "drop" ? `${l.qty ?? 1} ${l.unit} · ${l.ml.toFixed(1)} mL` : `${l.ml} mL`} · ${ing?.abv ? `${ing.abv.toFixed(1)}% abv` : "no abv"} · ${lineCost ? money(lineCost) : "no cost"}`,
      leader: `M${edge - 4},${mid.toFixed(1)} L${edge + 30},${mid.toFixed(1)} L356,${ty.toFixed(1)} L374,${ty.toFixed(1)}`,
    };
  });

  // ---- editing helpers
  function patch(part: Partial<SpecDraft>) {
    setDraft((d) => ({ ...d, ...part }));
  }
  function setLine(i: number, part: Partial<SpecLine>) {
    setDraft((d) => {
      const next = d.lines.map((l, j) => {
        if (j !== i) return l;
        const merged = { ...l, ...part };
        // Keep ml in step with a unit/qty change so totals stay honest.
        if (merged.unit === "dash") merged.ml = (merged.qty ?? 1) * DASH_ML;
        else if (merged.unit === "drop") merged.ml = (merged.qty ?? 1) * DROP_ML;
        return merged;
      });
      return { ...d, lines: next };
    });
  }
  function removeLine(i: number) {
    setDraft((d) => ({ ...d, lines: d.lines.filter((_, j) => j !== i) }));
  }
  function addLine() {
    const first = sortedIngredients[0];
    if (!first) return;
    setDraft((d) => ({ ...d, lines: [...d.lines, { ingredientId: first.ingredientId, ml: 15, unit: "ml" }] }));
  }
  function moveLine(i: number, dir: -1 | 1) {
    setDraft((d) => {
      const j = i + dir;
      if (j < 0 || j >= d.lines.length) return d;
      const next = [...d.lines];
      const [a, b] = [next[i]!, next[j]!];
      next[i] = b;
      next[j] = a;
      return { ...d, lines: next };
    });
  }

  async function save() {
    setSaving(true);
    setMsg(null);
    try {
      const saved = await p.onSave({
        ...draft,
        style: draft.style.map((s) => s.trim()).filter(Boolean),
        // The server normalises too — this is so what comes back matches
        // what the field showed, rather than silently re-casing on save.
        tags: normaliseTags(draft.tags),
        lines: draft.lines.map((l) =>
          l.unit === "ml" ? { ingredientId: l.ingredientId, ml: l.ml, unit: "ml" as const } : l,
        ),
      });
      setMsg({ kind: "ok", text: `Saved as version ${saved.version}.` });
      setEditing(false);
    } catch (err) {
      const text = err instanceof ApiError && err.status === 409
        ? `${err.message}`
        : err instanceof Error ? err.message : "Save failed";
      setMsg({ kind: "err", text });
    } finally {
      setSaving(false);
    }
  }

  async function savePriceNow() {
    setMsg(null);
    try {
      const n = price.trim() === "" ? null : Number(price);
      if (n !== null && !Number.isFinite(n)) throw new Error("Price must be a number");
      await p.onSavePrice(n, available);
      setMsg({ kind: "ok", text: "Venue price saved." });
    } catch (err) {
      setMsg({ kind: "err", text: err instanceof Error ? err.message : "Could not save the price" });
    }
  }

  const specRows: [string, string][] = [
    ["Glass", shown.glass || "—"],
    ["Method", shown.method || "—"],
    ["Ice", shown.ice || "—"],
    ["Garnish", shown.garnish || "—"],
    ["Batch", shown.batch || "—"],
    ["Standard drinks", totals.standardDrinks.toFixed(1)],
    ["Pour cost", pourCost !== null ? pct(pourCost) : "—"],
    ["Created by", shown.creator || "—"],
    ["Added", shown.added ?? "—"],
    ...(shown.sheetGp ? ([["GP on the sheet", shown.sheetGp]] as [string, string][]) : []),
    ["Version", `v${p.spec.version}${p.spec.reviewed ? "" : " · unreviewed import"}`],
  ];

  return (
    <div className="split">
      <section className="pane" style={{ padding: "20px 8px 20px 20px" }}>
        <div className="row spec-nav" style={{ paddingBottom: 10, borderBottom: "1px solid var(--ln)" }}>
          <button className="btn" onClick={p.onBack}>← All recipes</button>
          <span className="spacer" />
          {!editing && p.onPrev && <button className="btn" style={{ color: "var(--t2)" }} onClick={p.onPrev}>← Prev</button>}
          {!editing && p.onNext && <button className="btn" style={{ color: "var(--t2)" }} onClick={p.onNext}>Next →</button>}
          {!editing && p.canBatch !== false && (
            <button className="btn" aria-pressed={tool === "batch"} onClick={() => setTool(tool === "batch" ? "build" : "batch")}>
              Batch &amp; scale
            </button>
          )}
          {!editing && (
            <span className="share-wrap">
              <button className="btn" aria-expanded={sharing} onClick={() => setSharing((v) => !v)}>Share ▾</button>
              {sharing && (
                <span className="share-menu" role="menu">
                  <button
                    role="menuitem"
                    onClick={async () => {
                      setSharing(false);
                      // The address is the spec's, org and venue included, so it
                      // opens straight onto this drink for anyone in the team.
                      const ok = await copy(window.location.href);
                      if (ok) setMsg({ kind: "ok", text: "Link copied. It opens this spec for anyone signed in to the organisation." });
                      else setManualCopy(window.location.href);
                    }}
                  >
                    Copy link for the team
                  </button>
                  <button role="menuitem" onClick={() => { setSharing(false); window.print(); }}>
                    Print for the back bar
                  </button>
                  {p.canShare && (
                    <button
                      role="menuitem"
                      onClick={async () => {
                        setSharing(false);
                        const text = publicRecipeText(publicRecipe(p.spec, ingredients), p.placeName);
                        const ok = await copy(text);
                        if (ok) setMsg({ kind: "ok", text: "Caption copied: name, style, ingredients and the serve — no quantities, costs or prices." });
                        else setManualCopy(text);
                      }}
                    >
                      Copy a caption for social
                    </button>
                  )}
                </span>
              )}
            </span>
          )}
          {p.canEdit && !editing && (
            <button className="btn" onClick={() => { setDraft(draftOf(p.spec, undefined, p.withinItem?.ownTags)); setEditing(true); setMsg(null); setTool("build"); }}>Edit spec</button>
          )}
          {editing && (
            <>
              <button className="btn" onClick={() => { setEditing(false); setDraft(draftOf(p.spec, undefined, p.withinItem?.ownTags)); setMsg(null); }} disabled={saving}>Cancel</button>
              <button className="btn primary" style={{ height: 48 }} onClick={save} disabled={saving}>
                {saving ? "Saving…" : `Save as v${p.spec.version + 1}`}
              </button>
            </>
          )}
        </div>

        <div className="spec-head">
          <div style={{ minWidth: 0 }}>
            <div className="row" style={{ gap: 8 }}>
              <span className="dot" style={{ background: statusColour(shown.status) }} />
              <span className="label" style={{ color: statusColour(shown.status) }}>{STATUS_LABEL[shown.status]}</span>
              <span className="label">· {shown.style.join(" · ") || "no style"}</span>
              {headTags.map((t) => {
                const own = editing ? draft.tags.includes(t) : ownTags?.includes(t);
                return (
                  <span
                    key={t}
                    className={`label tag-pill${own ? " own" : ""}`}
                    title={own ? "This recipe's tag" : undefined}
                  >
                    {t}
                  </span>
                );
              })}
            </div>
            {!editing && primary && !p.withinItem && (
              <button className="variant-of" onClick={() => p.onOpen(primary.specId)}>
                Variant of {primary.name} →
              </button>
            )}
            {editing && !p.withinItem?.house ? (
              <input type="text" value={draft.name} onChange={(e) => patch({ name: e.target.value })} className="serif spec-name-input" style={{ fontSize: 28, height: 52, marginTop: 4 }} />
            ) : (
              <div className="spec-name">{shown.name}</div>
            )}
            {vsHouse && (
              <div className="label" style={{ marginTop: 6, lineHeight: 1.6 }}>
                {vsHouse.length === 0 ? "The same build as House" : `From House: ${vsHouse.join(" · ")}`}
              </div>
            )}
          </div>
          <span className="spacer" />
          <div className="stat">
            <div className="label">Cost / serve</div>
            <div className="v">{money(totals.cost)}</div>
          </div>
          <div className="vline" />
          <div className="stat">
            <div className="label">{priceNum ? `At ${money(priceNum)}` : "No venue price"}</div>
            <div className="v">{gp !== null ? pct(gp) : "—"}</div>
          </div>
          <div className="vline" />
          <div className="stat">
            <div className="label">ABV · std drinks</div>
            <div className="v" style={{ fontSize: 20 }}>{pct(totals.abv)} · {standardDrinks(totals.volumeMl, totals.abv).toFixed(1)}</div>
          </div>
        </div>

        {msg && <div className={`msg ${msg.kind}`} style={{ marginTop: 10 }}>{msg.text}</div>}
        {manualCopy && (
          <div className="msg" style={{ marginTop: 10 }}>
            <div className="label" style={{ marginBottom: 6 }}>The browser would not copy it; select and copy this</div>
            <textarea readOnly value={manualCopy} onFocus={(e) => e.currentTarget.select()} style={{ width: "100%" }} />
            <button className="btn sm" style={{ marginTop: 8 }} onClick={() => setManualCopy(null)}>Done</button>
          </div>
        )}

        {!editing && tool === "build" && (
          <div className="recipe-switch">
            <div className="row" role="group" aria-label="How to show the recipe">
              <button className="btn" aria-pressed={view === "card"} onClick={() => chooseView("card")}>Card</button>
              <button className="btn" aria-pressed={view === "exploded"} onClick={() => chooseView("exploded")}>Exploded build</button>
            </div>
            {scratch && (
              <>
                <div className="row" role="group" aria-label="How to read the build">
                  <button className="btn" aria-pressed={!fromScratch} onClick={() => setFromScratch(false)}>As served</button>
                  <button className="btn" aria-pressed={fromScratch} onClick={() => setFromScratch(true)}>From scratch</button>
                </div>
                <span className="label">
                  {fromScratch
                    ? `${housemade.map((h) => h.name).join(", ")} opened into what ${housemade.length === 1 ? "it is" : "they are"} made of`
                    : `Pours ${housemade.map((h) => h.name).join(", ")}`}
                </span>
              </>
            )}
          </div>
        )}

        <div className="scroll" style={{ marginTop: 8 }}>
          {!editing && tool === "batch" && p.canBatch !== false && <BatchTool spec={p.spec} ingredients={ingredients} price={priceNum} />}
          {!editing && tool === "build" && view === "card" && (
            <RecipeCard
              key={`${p.spec.specId}-${p.spec.version}-${fromScratch ? "scratch" : "served"}`}
              card={recipeCard(shown, ingredients, viewLines)}
              caption={`Recipe card${scratch ? (fromScratch ? " · from scratch" : " · as served") : ""} · ${viewLines.length} lines · ${viewTotals.volumeMl.toFixed(0)} mL`}
            />
          )}
          {!editing && tool === "build" && housemade.length > 0 && (
            <div className="batch-used">
              <div className="label" style={{ marginBottom: 8 }}>Batch items used · {housemade.length}</div>
              {housemade.map((h) => {
                const pour = shown.lines.filter((l) => l.ingredientId === h.ingredientId).reduce((sum, l) => sum + l.ml, 0);
                const parts = h.build?.lines ?? [];
                const total = parts.reduce((sum, l) => sum + l.ml, 0);
                const inner = (
                  <>
                    <div className="batch-used-head">
                      <span className="batch-used-name">{h.name}</span>
                      <span className="label">{+pour.toFixed(1)} mL a serve</span>
                    </div>
                    {parts.length > 0 ? (
                      <div className="batch-used-parts">
                        {parts.map((l, i) => (
                          <span key={i}>
                            {lineText(l, ingredients)}
                            {total > 0 && <span className="label"> · {Math.round((l.ml / total) * 100)}%</span>}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <div className="label">No recipe on file: costed by hand on the ingredient.</div>
                    )}
                    <div className="label batch-used-facts">
                      {h.build?.yieldMl ? `A batch makes ${h.build.yieldMl >= 1000 ? `${+(h.build.yieldMl / 1000).toFixed(2)} L` : `${h.build.yieldMl} mL`}` : ""}
                      {h.build?.yieldMl && h.build?.shelfLifeDays ? " · " : ""}
                      {h.build?.shelfLifeDays ? `keeps ${h.build.shelfLifeDays} day${h.build.shelfLifeDays === 1 ? "" : "s"}` : ""}
                      {p.onOpenBatch ? (h.build?.yieldMl || h.build?.shelfLifeDays ? " · " : "") + "Open on Prep › Batches →" : ""}
                    </div>
                  </>
                );
                return p.onOpenBatch ? (
                  <button key={h.ingredientId} type="button" className="batch-used-row" onClick={() => p.onOpenBatch!(h.ingredientId)}>{inner}</button>
                ) : (
                  <div key={h.ingredientId} className="batch-used-row">{inner}</div>
                );
              })}
            </div>
          )}
          {!editing && tool === "build" && view === "exploded" && (
            <Scaled width={640} height={470}><div style={{ position: "relative", width: 640, height: 470 }}>
              <svg width="640" height="470" viewBox="0 0 640 470" role="img" aria-label={`Exploded cross-section of the ${shown.name} build`} style={{ position: "absolute", left: 0, top: 0 }}>
                <defs>
                  <clipPath id="ab-bowl"><path d="M96,52 C96,168 130,214 178,222 L214,222 C262,214 296,168 296,52 Z" /></clipPath>
                  <clipPath id="ab-tall"><path d="M118,40 L118,204 C118,214 126,222 136,222 L256,222 C266,222 274,214 274,204 L274,40 Z" /></clipPath>
                </defs>
                {tall ? (
                  <g>
                    <path d="M118,40 L118,204 C118,214 126,222 136,222 L256,222 C266,222 274,214 274,204 L274,40" fill="none" stroke="var(--ln)" />
                    <path d="M118,40 L274,40" fill="none" stroke="var(--ln)" strokeDasharray="3 4" />
                  </g>
                ) : (
                  <g>
                    <path d="M96,52 C96,168 130,214 178,222 L214,222 C262,214 296,168 296,52 Z" fill="none" stroke="var(--ln)" />
                    <path d="M186,222 L186,392 M206,222 L206,392 M146,396 L246,396" fill="none" stroke="var(--ln)" />
                    <path d="M146,396 C146,404 152,408 160,408 L232,408 C240,408 246,404 246,396" fill="none" stroke="var(--ln)" />
                  </g>
                )}
                <g clipPath={tall ? "url(#ab-tall)" : "url(#ab-bowl)"}>
                  {strata.map((s) => (
                    <rect key={s.key} x={rx} y={s.y} width={rw} height={s.h} fill={s.fill} />
                  ))}
                </g>
                {strata.map((s) => (
                  <g key={s.key + "-l"}>
                    <path d={s.leader} fill="none" stroke="var(--t2)" />
                    <circle cx={edge - 4} cy={s.mid} r="2" fill="var(--t2)" />
                  </g>
                ))}
                <path d="M60,52 L60,222 M56,52 L64,52 M56,222 L64,222" fill="none" stroke="var(--t2)" />
                <foreignObject x="0" y="130" width="52" height="34" style={{ overflow: "visible" }}>
                  <div className="mono" style={{ width: 52, textAlign: "right", fontSize: 10, lineHeight: 1.5, color: "var(--t2)" }}>{viewTotals.volumeMl.toFixed(0)} mL<br />served</div>
                </foreignObject>
                <foreignObject x="96" y="440" width="520" height="20" style={{ overflow: "visible" }}>
                  <div className="ann" style={{ color: "var(--t2)" }}>Exploded build{scratch ? (fromScratch ? " · from scratch" : " · as served") : ""} · {viewLines.length} lines · {shown.method || "method not set"}</div>
                </foreignObject>
              </svg>
              <div style={{ position: "absolute", left: 378, top: top0, width: 250, display: "flex", flexDirection: "column", gap: pitch - 30, pointerEvents: "none" }}>
                {[...strata].reverse().map((s) => (
                  <div key={s.key + "-a"} style={{ height: 30, display: "flex", flexDirection: "column", justifyContent: "center", gap: 4 }}>
                    <div className="ann">{s.tag} — {s.name}</div>
                    <div className="ann-d">{s.detail}</div>
                  </div>
                ))}
              </div>
            </div></Scaled>
          )}

          {editing && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16, paddingRight: 12 }}>
              <div>
                <div className="label" style={{ marginBottom: 8 }}>Build · {totals.volumeMl.toFixed(0)} mL · {money(totals.cost)}</div>
                {draft.lines.map((l, i) => {
                  const ing = ingredients.get(l.ingredientId);
                  return (
                    <div className="line-row" key={i}>
                      <span className="swatch" style={{ background: colourOf(ing) }} />
                      <select value={l.ingredientId} onChange={(e) => setLine(i, { ingredientId: e.target.value })}>
                        {!ing && <option value={l.ingredientId}>{l.ingredientId} (unknown)</option>}
                        {sortedIngredients.map((o) => (
                          <option key={o.ingredientId} value={o.ingredientId}>{o.name}{o.reviewed ? "" : " *"}</option>
                        ))}
                      </select>
                      {l.unit === "dash" || l.unit === "drop" ? (
                        <input className="num-input" type="number" min={0} step={1} value={l.qty ?? 1} onChange={(e) => setLine(i, { qty: Number(e.target.value) })} />
                      ) : (
                        <input className="num-input" type="number" min={0} step={0.5} value={l.ml} onChange={(e) => setLine(i, { ml: Number(e.target.value) })} />
                      )}
                      <select value={l.unit} onChange={(e) => setLine(i, { unit: e.target.value as LineUnit, qty: l.qty ?? 1 })}>
                        <option value="ml">mL</option>
                        <option value="dash">dash</option>
                        <option value="drop">drop</option>
                        <option value="top">top</option>
                        <option value="each">each</option>
                      </select>
                      <span className="row" style={{ gap: 2 }}>
                        <button className="btn sm" style={{ padding: "0 6px" }} onClick={() => moveLine(i, -1)} aria-label="Move up">↑</button>
                        <button className="btn sm" style={{ padding: "0 6px" }} onClick={() => moveLine(i, 1)} aria-label="Move down">↓</button>
                        <button className="btn sm danger" style={{ padding: "0 6px" }} onClick={() => removeLine(i)} aria-label="Remove">×</button>
                      </span>
                    </div>
                  );
                })}
                <button className="btn dashed" style={{ marginTop: 10 }} onClick={addLine}>+ Add line</button>
                <div className="label" style={{ marginTop: 8 }}>* cost not yet confirmed for that ingredient</div>
              </div>

              <div className="grid2">
                {!p.withinItem && (
                <label className="field"><span>Variant of</span>
                  <select
                    value={draft.variantOf ?? ""}
                    disabled={ownVariants.length > 0}
                    onChange={(e) => patch({ variantOf: e.target.value || null })}
                  >
                    <option value="">
                      {ownVariants.length > 0
                        ? `Nothing: it has ${ownVariants.length} variant${ownVariants.length === 1 ? "" : "s"}`
                        : "Nothing: a primary recipe"}
                    </option>
                    {draft.variantOf && !primaries.some((s) => s.specId === draft.variantOf) && (
                      <option value={draft.variantOf}>{specsById.get(draft.variantOf)?.name ?? draft.variantOf}</option>
                    )}
                    {primaries.map((s) => <option key={s.specId} value={s.specId}>{s.name}</option>)}
                  </select>
                </label>
                )}
                <label className="field"><span>Status</span>
                  <select value={draft.status} onChange={(e) => patch({ status: e.target.value as SpecStatus })}>
                    {SPEC_STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                  </select>
                </label>
                {!p.withinItem && (
                  <>
                <label className="field"><span>Style (comma separated)</span>
                  <input type="text" value={draft.style.join(", ")} onChange={(e) => patch({ style: e.target.value.split(",").map((s) => s.trim()) })} />
                </label>
                <label className="field"><span>Tags (comma separated)</span>
                  <input type="text" value={draft.tags.join(", ")} onChange={(e) => patch({ tags: e.target.value.split(",").map((t) => t.trim()) })} />
                </label>
                  </>
                )}
                {p.withinItem && (
                  <label className="field">
                    {/* The recipe's own, not the drink's: "summer list" on
                        one variant and not another (#188). The item's tags
                        are edited on the page above. */}
                    <span>This recipe's tags (comma separated)</span>
                    <input type="text" value={draft.tags.join(", ")} onChange={(e) => patch({ tags: e.target.value.split(",").map((t) => t.trim()) })} />
                  </label>
                )}
                <label className="field"><span>Glass</span><input type="text" value={draft.glass} onChange={(e) => patch({ glass: e.target.value })} /></label>
                <label className="field"><span>Method</span><input type="text" value={draft.method} onChange={(e) => patch({ method: e.target.value })} /></label>
                <label className="field"><span>Ice</span><input type="text" value={draft.ice} onChange={(e) => patch({ ice: e.target.value })} /></label>
                <label className="field"><span>Garnish</span><input type="text" value={draft.garnish} onChange={(e) => patch({ garnish: e.target.value })} /></label>
                <label className="field"><span>Batch</span><input type="text" value={draft.batch} onChange={(e) => patch({ batch: e.target.value })} /></label>
                {!p.withinItem && (
                  <>
                <label className="field"><span>Created by</span><input type="text" value={draft.creator} onChange={(e) => patch({ creator: e.target.value })} /></label>
                <label className="field"><span>Added</span><input type="date" value={draft.added ?? ""} onChange={(e) => patch({ added: e.target.value || null })} /></label>
                  </>
                )}
                <label className="field"><span>Sundries per serve ($)</span><input className="num-input" style={{ width: "100%" }} type="number" min={0} step={0.05} value={draft.sundries} onChange={(e) => patch({ sundries: Number(e.target.value) })} /></label>
              </div>
              {!p.withinItem && (
                <label className="field"><span>History and inspiration</span><textarea value={draft.history} onChange={(e) => patch({ history: e.target.value })} /></label>
              )}
              <label className="field"><span>Notes</span><textarea value={draft.notes} onChange={(e) => patch({ notes: e.target.value })} /></label>
              <div>
                <div className="label" style={{ marginBottom: 6 }}>3-2-1 · how to talk about it</div>
                <div className="talk-editor">
                  <label className="field"><span>One · a sentence for every guest</span>
                    <textarea rows={2} value={draft.talk?.one ?? ""} onChange={(e) => patch({ talk: { one: e.target.value, two: draft.talk?.two ?? "", three: draft.talk?.three ?? "" } })} />
                  </label>
                  <label className="field"><span>Two · two sentences for the interested</span>
                    <textarea rows={3} value={draft.talk?.two ?? ""} onChange={(e) => patch({ talk: { one: draft.talk?.one ?? "", two: e.target.value, three: draft.talk?.three ?? "" } })} />
                  </label>
                  <label className="field"><span>Three · the deep dive</span>
                    <textarea rows={5} value={draft.talk?.three ?? ""} onChange={(e) => patch({ talk: { one: draft.talk?.one ?? "", two: draft.talk?.two ?? "", three: e.target.value } })} />
                  </label>
                </div>
              </div>
              {p.spec.sourceText && (
                <details>
                  <summary className="label" style={{ cursor: "pointer" }}>Original recipe text from the sheet</summary>
                  <pre className="mono" style={{ fontSize: 11, color: "var(--t2)", whiteSpace: "pre-wrap" }}>{p.spec.sourceText}</pre>
                </details>
              )}
            </div>
          )}
        </div>
      </section>

      <section className="aside">
        <div className="scroll pad" style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          {showFamily && (
            <div>
              <div className="label" style={{ marginBottom: 8 }}>
                {primary
                  ? `The ${primary.name} family`
                  : ownVariants.length > 0 ? `Variants · ${ownVariants.length}` : "Variants"}
              </div>
              <div className="variant-list">
                {primary && (
                  <button className="variant-link" onClick={() => p.onOpen(primary.specId)}>
                    <span className="dot" style={{ background: statusColour(primary.status) }} />
                    <span className="vn">{primary.name}</span>
                    <span className="label">primary</span>
                  </button>
                )}
                {family.map((v) =>
                  v.specId === p.spec.specId ? (
                    <span key={v.specId} className="variant-link here" aria-current="page">
                      <span className="dot" style={{ background: statusColour(v.status) }} />
                      <span className="vn">{v.name}</span>
                      <span className="label">this one</span>
                    </span>
                  ) : (
                    <button key={v.specId} className="variant-link" onClick={() => p.onOpen(v.specId)}>
                      <span className="dot" style={{ background: statusColour(v.status) }} />
                      <span className="vn">{v.name}</span>
                    </button>
                  ),
                )}
                {!primary && ownVariants.length === 0 && (
                  <span className="muted" style={{ fontSize: 13, lineHeight: 1.5 }}>
                    None. A variant is this drink with something swapped, such as a Rum Negroni or
                    a Boulevardier, with its own build and price.
                  </span>
                )}
              </div>
              {p.canEdit && !p.spec.variantOf && !editing && variantName === null && (
                <button
                  className="btn dashed sm"
                  style={{ marginTop: 10 }}
                  onClick={() => { setVariantName(""); setVariantErr(null); }}
                >
                  + Add a variant
                </button>
              )}
              {p.canEdit && !p.spec.variantOf && !editing && variantName !== null && (
                <form
                  className="variant-new"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    setVariantErr(null);
                    try {
                      await p.onNewVariant(variantName);
                    } catch (err) {
                      setVariantErr(err instanceof Error ? err.message : "Could not create the variant");
                    }
                  }}
                >
                  <input
                    type="text"
                    autoFocus
                    required
                    placeholder={`e.g. Rum ${p.spec.name}`}
                    value={variantName}
                    onChange={(e) => setVariantName(e.target.value)}
                  />
                  <button className="btn sm" type="submit">Create</button>
                  <button className="btn sm" type="button" onClick={() => setVariantName(null)}>Cancel</button>
                  <div className="label" style={{ flexBasis: "100%" }}>Starts as a copy of this build, to edit.</div>
                </form>
              )}
              {variantErr && <div className="msg err" style={{ marginTop: 8 }}>{variantErr}</div>}
            </div>
          )}
          {showFamily && <div className="rule" />}
          {!p.withinItem && (
            <>
              <div>
                <div className="label" style={{ marginBottom: 8 }}>History and inspiration</div>
                <div style={{ fontSize: 14, lineHeight: 1.55 }}>{shown.history || <span className="muted">Nothing on file.</span>}</div>
              </div>
              <div className="rule" />
            </>
          )}
          {hasTalk(shown.talk) && (
            <>
              <div className="talk">
                <div className="label" style={{ marginBottom: 8 }}>3-2-1</div>
                {shown.talk.one && <p className="talk-one">{shown.talk.one}</p>}
                {shown.talk.two && <p className="talk-two"><span className="label">Two</span>{shown.talk.two}</p>}
                {shown.talk.three && <p className="talk-three"><span className="label">Three</span>{shown.talk.three}</p>}
              </div>
              <div className="rule" />
            </>
          )}
          <SpecAroma spec={shown} ingredients={ingredients} />
          <div className="rule" />
          {specRows.map(([k, v]) => (
            <div className="kv" key={k}><span className="k">{k}</span><span className="v">{v}</span></div>
          ))}
          {shown.notes && (
            <div>
              <div className="label" style={{ marginBottom: 8 }}>Notes</div>
              <div style={{ fontSize: 13, lineHeight: 1.5, whiteSpace: "pre-wrap", color: "var(--t2)" }}>{shown.notes}</div>
            </div>
          )}
          {p.canPrice && (
            <div>
              <div className="label" style={{ marginBottom: 8 }}>Venue price</div>
              <div className="row">
                <input className="num-input" type="number" min={0} step={0.5} value={price} onChange={(e) => setPrice(e.target.value)} placeholder="—" />
                <label className="row" style={{ gap: 6, fontSize: 12 }}>
                  <input type="checkbox" checked={available} onChange={(e) => setAvailable(e.target.checked)} /> on the menu
                </label>
                <span className="spacer" />
                <button className="btn sm" onClick={savePriceNow}>Save</button>
              </div>
            </div>
          )}
          <div>
            <div className="label" style={{ marginBottom: 8 }}>Versions</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {versions === null && <span className="label">Loading…</span>}
              {versions && versions.length === 0 && <span className="label">No history yet</span>}
              {versions && [...versions].reverse().map((v, i, all) => {
                // What this version changed from the one before it.
                const earlier = all[i + 1];
                const changed = earlier ? recipeDiff(earlier.lines, v.lines, ingredients) : [];
                return (
                <div key={v.version} style={{ display: "flex", gap: 10 }}>
                  <span className="num" style={{ fontSize: 11, color: "var(--t2)", width: 26, flex: "0 0 26px" }}>v{v.version}</span>
                  <span style={{ flex: 1 }}>
                    <span style={{ display: "block", fontSize: 13, lineHeight: 1.4 }}>
                      {v.lines.length} lines · {v.status}{v.updatedBy === "import-script" ? " · imported from the sheet" : ""}
                    </span>
                    {changed.length > 0 && (
                      <span style={{ display: "block", fontSize: 12, lineHeight: 1.5, color: "var(--t2)" }}>{changed.join(" · ")}</span>
                    )}
                    <span className="label" style={{ display: "block", marginTop: 2, letterSpacing: "0.08em" }}>{shortDate(v.updatedAt)} · {v.updatedBy === "import-script" ? "import" : v.updatedBy.slice(0, 8)}</span>
                  </span>
                </div>
                );
              })}
            </div>
          </div>
        </div>
        <div className="foot">
          {p.onRevise && <button className="btn primary" onClick={p.onRevise}>Revise in development</button>}
        </div>
      </section>

      {/* Only on paper (theme.css @media print): the spec as a back-bar card.
          Quantities, because it is for the team; no costs, GP or prices,
          because a printed sheet leaves the building on its own. */}
      <div className="spec-print" aria-hidden="true">
        <div className="sp-name">{p.spec.name}</div>
        <div className="sp-style">{p.spec.style.join(" · ")}</div>
        <table>
          <tbody>
            {p.spec.lines.map((l, i) => {
              const ing = ingredients.get(l.ingredientId);
              return (
                <tr key={i}>
                  <td className="sp-qty">
                    {l.unit === "dash" || l.unit === "drop" ? `${l.qty ?? 1} ${l.unit}` : l.unit === "top" ? "top" : `${+l.ml.toFixed(1)} mL`}
                  </td>
                  <td>{ing?.name ?? l.ingredientId}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <dl>
          {([["Glass", p.spec.glass], ["Method", p.spec.method], ["Ice", p.spec.ice], ["Garnish", p.spec.garnish], ["Batch", p.spec.batch]] as const)
            .filter(([, v]) => v)
            .map(([k, v]) => (
              <div key={k}><dt>{k}</dt><dd>{v}</dd></div>
            ))}
        </dl>
        {p.spec.notes && <div className="sp-notes">{p.spec.notes}</div>}
        <div className="sp-foot">{[p.placeName, `v${p.spec.version}`].filter(Boolean).join(" · ")}</div>
      </div>
    </div>
  );
}
